import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import AdminShell from "../../components/AdminShell";
import { api, formatApiError, formatCDF } from "../../lib/api";
import { Badge, Button, Card, Input, Label, StatusBadge } from "../../components/ui-kit";
import { CheckCircle, ForkKnife, Package, PencilSimple, Storefront, Trash, UserGear } from "@phosphor-icons/react";

const emptyMerchant = (cityId = "") => ({
  name: "",
  type: "restaurant",
  city_id: cityId,
  phone: "",
  email: "",
  address: "",
  lat: "",
  lng: "",
  description: "",
  logo_url: "",
  cover_url: "",
  mpesa_phone: "",
  airtel_phone: "",
  orange_phone: "",
  payment_instructions: "",
  mobile_money_api_enabled: false,
  active: true,
  commission_rate: 0.10,
  preparation_time_min: 20,
});

const emptyProduct = {
  name: "",
  description: "",
  price: "",
  image_url: "",
  image_file: null,
  category: "",
  available: true,
};

const emptyAgent = (cityId = "") => ({
  name: "",
  phone: "",
  email: "",
  city_id: cityId,
  title: "Agent terrain",
  active: true,
  permissions: ["support"],
  notes: "",
});

const AGENT_PERMISSION_OPTIONS = [
  {
    key: "support",
    label: "Support client",
    desc: "Voir les litiges, assister les clients et suivre les alertes support.",
  },
  {
    key: "merchant_onboarding",
    label: "Partenaires",
    desc: "Aider a enregistrer restaurants, supermarches et boutiques.",
  },
  {
    key: "driver_onboarding",
    label: "Chauffeurs",
    desc: "Suivre les dossiers chauffeurs/livreurs et les documents a valider.",
  },
  {
    key: "delivery_ops",
    label: "Operations livraison",
    desc: "Confirmer les commandes et lancer la recherche de livreur.",
  },
  {
    key: "finance",
    label: "Finance terrain",
    desc: "Suivre les paiements, preuves, commissions et confirmations terrain.",
  },
];

function cleanOptional(value) {
  const text = String(value || "").trim();
  return text ? text : null;
}

function permissionLabel(key) {
  return AGENT_PERMISSION_OPTIONS.find((item) => item.key === key)?.label || key;
}

export default function AdminMarketplace() {
  const [tab, setTab] = useState("partners");
  const [cities, setCities] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [orders, setOrders] = useState([]);
  const [agents, setAgents] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedMerchantId, setSelectedMerchantId] = useState("");
  const [editingMerchantId, setEditingMerchantId] = useState("");
  const [editingAgentId, setEditingAgentId] = useState("");
  const [merchantForm, setMerchantForm] = useState(emptyMerchant());
  const [productForm, setProductForm] = useState(emptyProduct);
  const [productQuery, setProductQuery] = useState("");
  const [productCategoryFilter, setProductCategoryFilter] = useState("all");
  const [agentForm, setAgentForm] = useState(emptyAgent());
  const selectedMerchant = useMemo(
    () => merchants.find((item) => item.id === selectedMerchantId) || merchants[0] || null,
    [merchants, selectedMerchantId],
  );
  const productCategories = useMemo(() => {
    const categories = products.map((product) => product.category || "General").filter(Boolean);
    return Array.from(new Set(categories)).sort((a, b) => a.localeCompare(b));
  }, [products]);
  const filteredProducts = useMemo(() => {
    const q = productQuery.trim().toLowerCase();
    return products.filter((product) =>
      (productCategoryFilter === "all" || (product.category || "General") === productCategoryFilter) &&
      (!q || [product.name, product.description, product.category].filter(Boolean).some((value) => String(value).toLowerCase().includes(q)))
    );
  }, [productCategoryFilter, productQuery, products]);

  const load = async () => {
    try {
      const [cityRes, merchantRes, orderRes, agentRes] = await Promise.all([
        api.get("/admin/cities"),
        api.get("/admin/merchants"),
        api.get("/admin/merchant-orders"),
        api.get("/admin/agents"),
      ]);
      const cityList = Array.isArray(cityRes.data) ? cityRes.data : [];
      const merchantList = Array.isArray(merchantRes.data) ? merchantRes.data : [];
      setCities(cityList);
      setMerchants(merchantList);
      setOrders(Array.isArray(orderRes.data) ? orderRes.data : []);
      setAgents(Array.isArray(agentRes.data) ? agentRes.data : []);
      const defaultCity = cityList.find((city) => city.active !== false)?.id || "";
      setMerchantForm((current) => current.city_id ? current : emptyMerchant(defaultCity));
      setAgentForm((current) => current.city_id ? current : emptyAgent(defaultCity));
      setSelectedMerchantId((current) => current || merchantList[0]?.id || "");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const loadProducts = async (merchantId) => {
    if (!merchantId) {
      setProducts([]);
      return;
    }
    try {
      const { data } = await api.get(`/merchants/${merchantId}/products`);
      setProducts(Array.isArray(data) ? data : []);
    } catch {
      setProducts([]);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    loadProducts(selectedMerchant?.id);
  }, [selectedMerchant?.id]);

  const submitMerchant = async () => {
    try {
      const payload = {
        ...merchantForm,
        lat: Number(merchantForm.lat),
        lng: Number(merchantForm.lng),
        commission_rate: Number(merchantForm.commission_rate),
        preparation_time_min: Number(merchantForm.preparation_time_min),
        phone: cleanOptional(merchantForm.phone),
        email: cleanOptional(merchantForm.email),
        description: cleanOptional(merchantForm.description),
        logo_url: cleanOptional(merchantForm.logo_url),
        cover_url: cleanOptional(merchantForm.cover_url),
        mpesa_phone: cleanOptional(merchantForm.mpesa_phone),
        airtel_phone: cleanOptional(merchantForm.airtel_phone),
        orange_phone: cleanOptional(merchantForm.orange_phone),
        payment_instructions: cleanOptional(merchantForm.payment_instructions),
        mobile_money_api_enabled: Boolean(merchantForm.mobile_money_api_enabled),
      };
      if (editingMerchantId) {
        await api.put(`/admin/merchants/${editingMerchantId}`, payload);
        toast.success("Partenaire modifie");
      } else {
        await api.post("/admin/merchants", payload);
        toast.success("Partenaire cree");
      }
      setEditingMerchantId("");
      setMerchantForm(emptyMerchant(cities[0]?.id || ""));
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const editMerchant = (merchant) => {
    setEditingMerchantId(merchant.id);
    setMerchantForm({
      name: merchant.name || "",
      type: merchant.type || "restaurant",
      city_id: merchant.city_id || "",
      phone: merchant.phone || "",
      email: merchant.email || "",
      address: merchant.address || "",
      lat: merchant.lat || "",
      lng: merchant.lng || "",
      description: merchant.description || "",
      logo_url: merchant.logo_url || "",
      cover_url: merchant.cover_url || "",
      mpesa_phone: merchant.mpesa_phone || "",
      airtel_phone: merchant.airtel_phone || "",
      orange_phone: merchant.orange_phone || "",
      payment_instructions: merchant.payment_instructions || "",
      mobile_money_api_enabled: merchant.mobile_money_api_enabled === true,
      active: merchant.active !== false,
      commission_rate: merchant.commission_rate ?? 0.10,
      preparation_time_min: merchant.preparation_time_min ?? 20,
    });
    setTab("partners");
  };

  const deleteMerchant = async (merchantId) => {
    try {
      await api.delete(`/admin/merchants/${merchantId}`);
      toast.success("Partenaire desactive");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const submitProduct = async () => {
    if (!selectedMerchant?.id) {
      toast.error("Selectionnez un partenaire");
      return;
    }
    try {
      const { data: product } = await api.post(`/admin/merchants/${selectedMerchant.id}/products`, {
        name: productForm.name.trim(),
        price: Number(productForm.price),
        description: cleanOptional(productForm.description),
        image_url: cleanOptional(productForm.image_url),
        category: cleanOptional(productForm.category),
        available: productForm.available !== false,
      });
      if (productForm.image_file && product?.id) {
        const form = new FormData();
        form.append("file", productForm.image_file);
        await api.post(`/admin/merchant-products/${product.id}/image`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success("Produit ajoute");
      setProductForm(emptyProduct);
      loadProducts(selectedMerchant.id);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteProduct = async (productId) => {
    try {
      await api.delete(`/admin/merchant-products/${productId}`);
      toast.success("Produit masque");
      loadProducts(selectedMerchant?.id);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const confirmOrder = async (orderId) => {
    try {
      await api.post(`/admin/merchant-orders/${orderId}/confirm`, { note: "Commande confirmee par l'administration" });
      toast.success("Livraison creee pour la commande");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const cancelOrder = async (orderId) => {
    try {
      await api.post(`/admin/merchant-orders/${orderId}/cancel`, { note: "Commande annulee par l'administration" });
      toast.success("Commande annulee");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const confirmPayment = async (orderId) => {
    try {
      await api.post(`/admin/merchant-orders/${orderId}/payment/confirm`, { note: "Paiement confirme par l'administration" });
      toast.success("Paiement confirme");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const rejectPayment = async (orderId) => {
    try {
      await api.post(`/admin/merchant-orders/${orderId}/payment/reject`, { note: "Capture non confirmee. Merci de renvoyer une preuve lisible." });
      toast.success("Paiement refuse");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const submitAgent = async () => {
    try {
      const payload = {
        ...agentForm,
        email: cleanOptional(agentForm.email),
        notes: cleanOptional(agentForm.notes),
        permissions: agentForm.permissions?.length ? agentForm.permissions : ["support"],
      };
      if (editingAgentId) {
        await api.put(`/admin/agents/${editingAgentId}`, payload);
        toast.success("Agent modifie");
      } else {
        await api.post("/admin/agents", payload);
        toast.success("Agent ajoute");
      }
      setEditingAgentId("");
      setAgentForm(emptyAgent(cities[0]?.id || ""));
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const editAgent = (agent) => {
    setEditingAgentId(agent.id);
    setAgentForm({
      name: agent.name || "",
      phone: agent.phone || "",
      email: agent.email || "",
      city_id: agent.city_id || cities[0]?.id || "",
      title: agent.title || "Agent terrain",
      active: agent.active !== false,
      permissions: agent.permissions?.length ? agent.permissions : ["support"],
      notes: agent.notes || "",
    });
    setTab("agents");
  };

  const toggleAgentPermission = (permission) => {
    setAgentForm((current) => {
      const permissions = new Set(current.permissions || []);
      if (permissions.has(permission)) {
        permissions.delete(permission);
      } else {
        permissions.add(permission);
      }
      return { ...current, permissions: Array.from(permissions) };
    });
  };

  const disableAgent = async (agentId) => {
    try {
      await api.delete(`/admin/agents/${agentId}`);
      toast.success("Agent desactive");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const tabs = [
    { key: "partners", label: "Partenaires", icon: Storefront },
    { key: "products", label: "Produits", icon: Package },
    { key: "orders", label: "Commandes", icon: CheckCircle },
    { key: "agents", label: "Agents", icon: UserGear },
  ];

  return (
    <AdminShell title="Marketplace">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] font-black uppercase tracking-widest text-primary">Restaurants, supermarches, agents</div>
          <h1 className="font-heading text-3xl font-black tracking-tighter sm:text-4xl">Marketplace Nexora</h1>
          <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
            Creez les partenaires, ajoutez les produits, confirmez les commandes et envoyez automatiquement un livreur Nexora.
          </p>
        </div>
        <Badge tone="success">{merchants.filter((item) => item.active !== false).length} partenaires actifs</Badge>
      </div>

      <div className="mt-6 grid gap-2 sm:grid-cols-4">
        {tabs.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.key}
              onClick={() => setTab(item.key)}
              className={`flex min-h-12 items-center justify-center gap-2 rounded-2xl border px-3 py-2 text-xs font-black uppercase tracking-widest ${tab === item.key ? "border-primary/60 bg-primary text-primary-foreground" : "border-white/10 bg-white/5 text-muted-foreground"}`}
            >
              <Icon size={17} weight="bold" />
              {item.label}
            </button>
          );
        })}
      </div>

      {tab === "partners" && (
        <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_1.2fr]">
          <Card className="p-5">
            <h2 className="font-heading text-2xl font-black">{editingMerchantId ? "Modifier partenaire" : "Nouveau partenaire"}</h2>
            <div className="mt-4 grid gap-3">
              <div>
                <Label>Nom</Label>
                <Input value={merchantForm.name} onChange={(e) => setMerchantForm({ ...merchantForm, name: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Type</Label>
                  <select value={merchantForm.type} onChange={(e) => setMerchantForm({ ...merchantForm, type: e.target.value })} className="w-full rounded-xl border border-white/10 bg-background px-4 py-3 text-sm">
                    <option value="restaurant">Restaurant</option>
                    <option value="supermarket">Supermarche</option>
                  </select>
                </div>
                <div>
                  <Label>Ville</Label>
                  <select value={merchantForm.city_id} onChange={(e) => setMerchantForm({ ...merchantForm, city_id: e.target.value })} className="w-full rounded-xl border border-white/10 bg-background px-4 py-3 text-sm">
                    {cities.map((city) => <option key={city.id} value={city.id}>{city.name}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <Label>Adresse</Label>
                <Input value={merchantForm.address} onChange={(e) => setMerchantForm({ ...merchantForm, address: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Latitude</Label>
                  <Input type="number" value={merchantForm.lat} onChange={(e) => setMerchantForm({ ...merchantForm, lat: e.target.value })} />
                </div>
                <div>
                  <Label>Longitude</Label>
                  <Input type="number" value={merchantForm.lng} onChange={(e) => setMerchantForm({ ...merchantForm, lng: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Telephone</Label>
                  <Input value={merchantForm.phone} onChange={(e) => setMerchantForm({ ...merchantForm, phone: e.target.value })} />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input value={merchantForm.email} onChange={(e) => setMerchantForm({ ...merchantForm, email: e.target.value })} />
                </div>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-3">
                <div className="mb-3 text-[10px] font-black uppercase tracking-widest text-primary">Comptes de paiement partenaire</div>
                <div className="grid gap-3 sm:grid-cols-3">
                  <div>
                    <Label>M-Pesa</Label>
                    <Input value={merchantForm.mpesa_phone} onChange={(e) => setMerchantForm({ ...merchantForm, mpesa_phone: e.target.value })} placeholder="+243..." />
                  </div>
                  <div>
                    <Label>Airtel Money</Label>
                    <Input value={merchantForm.airtel_phone} onChange={(e) => setMerchantForm({ ...merchantForm, airtel_phone: e.target.value })} placeholder="+243..." />
                  </div>
                  <div>
                    <Label>Orange Money</Label>
                    <Input value={merchantForm.orange_phone} onChange={(e) => setMerchantForm({ ...merchantForm, orange_phone: e.target.value })} placeholder="+243..." />
                  </div>
                </div>
                <div className="mt-3">
                  <Label>Instructions paiement</Label>
                  <Input
                    value={merchantForm.payment_instructions}
                    onChange={(e) => setMerchantForm({ ...merchantForm, payment_instructions: e.target.value })}
                    placeholder="Envoyer au numero puis ajouter la capture dans la commande."
                  />
                </div>
                <label className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={merchantForm.mobile_money_api_enabled}
                    onChange={(e) => setMerchantForm({ ...merchantForm, mobile_money_api_enabled: e.target.checked })}
                  />
                  API Mobile Money activee pour ce partenaire
                </label>
              </div>
              <div>
                <Label>Description</Label>
                <Input value={merchantForm.description} onChange={(e) => setMerchantForm({ ...merchantForm, description: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Commission</Label>
                  <Input type="number" step="0.01" value={merchantForm.commission_rate} onChange={(e) => setMerchantForm({ ...merchantForm, commission_rate: e.target.value })} />
                </div>
                <div>
                  <Label>Preparation min</Label>
                  <Input type="number" value={merchantForm.preparation_time_min} onChange={(e) => setMerchantForm({ ...merchantForm, preparation_time_min: e.target.value })} />
                </div>
              </div>
              <Button onClick={submitMerchant}>{editingMerchantId ? "Sauvegarder" : "Creer partenaire"}</Button>
              {editingMerchantId && (
                <Button variant="outline" onClick={() => { setEditingMerchantId(""); setMerchantForm(emptyMerchant(cities[0]?.id || "")); }}>
                  Annuler modification
                </Button>
              )}
            </div>
          </Card>

          <div className="grid gap-3">
            {merchants.map((merchant) => {
              const Icon = merchant.type === "restaurant" ? ForkKnife : Storefront;
              return (
                <Card key={merchant.id} className="p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex min-w-0 items-start gap-3">
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/15 text-primary">
                        <Icon size={22} weight="duotone" />
                      </span>
                      <div className="min-w-0">
                        <div className="truncate font-heading text-lg font-black">{merchant.name}</div>
                        <div className="mt-1 text-xs text-muted-foreground">{merchant.city_name} - {merchant.address}</div>
                        <div className="mt-2 flex flex-wrap gap-2">
                          <Badge tone={merchant.active === false ? "danger" : "success"}>{merchant.active === false ? "Inactif" : "Actif"}</Badge>
                          <Badge>{merchant.product_count || 0} produits</Badge>
                          <Badge tone="primary">{Math.round(Number(merchant.commission_rate || 0) * 100)}% commission</Badge>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => editMerchant(merchant)} className="rounded-full bg-white/10 p-2"><PencilSimple size={17} /></button>
                      <button onClick={() => deleteMerchant(merchant.id)} className="rounded-full bg-destructive/15 p-2 text-destructive"><Trash size={17} /></button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {tab === "products" && (
        <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_1.2fr]">
          <Card className="p-5">
            <h2 className="font-heading text-2xl font-black">Ajouter produit</h2>
            <div className="mt-4 grid gap-3">
              <div>
                <Label>Partenaire</Label>
                <select value={selectedMerchant?.id || ""} onChange={(e) => setSelectedMerchantId(e.target.value)} className="w-full rounded-xl border border-white/10 bg-background px-4 py-3 text-sm">
                  {merchants.map((merchant) => <option key={merchant.id} value={merchant.id}>{merchant.name}</option>)}
                </select>
              </div>
              <div>
                <Label>Nom produit</Label>
                <Input value={productForm.name} onChange={(e) => setProductForm({ ...productForm, name: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Prix CDF</Label>
                  <Input type="number" value={productForm.price} onChange={(e) => setProductForm({ ...productForm, price: e.target.value })} />
                </div>
                <div>
                  <Label>Categorie</Label>
                  <Input value={productForm.category} onChange={(e) => setProductForm({ ...productForm, category: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Input value={productForm.description} onChange={(e) => setProductForm({ ...productForm, description: e.target.value })} />
              </div>
              <div>
                <Label>Image produit</Label>
                <div className="grid gap-2">
                  <Input value={productForm.image_url} onChange={(e) => setProductForm({ ...productForm, image_url: e.target.value })} placeholder="Lien image optionnel" />
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(e) => setProductForm({ ...productForm, image_file: e.target.files?.[0] || null })}
                    className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-xs"
                  />
                  {(productForm.image_file || productForm.image_url) && (
                    <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 p-2">
                      <div className="h-16 w-16 overflow-hidden rounded-xl bg-black/30">
                        <img
                          src={productForm.image_file ? URL.createObjectURL(productForm.image_file) : productForm.image_url}
                          alt=""
                          className="h-full w-full object-cover"
                        />
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {productForm.image_file ? productForm.image_file.name : "Image externe"}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <Button onClick={submitProduct}><Package size={18} weight="bold" /> Ajouter produit</Button>
            </div>
          </Card>

          <div className="grid gap-3">
            <Card className="p-3">
              <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
                <Input value={productQuery} onChange={(e) => setProductQuery(e.target.value)} placeholder="Rechercher un produit..." />
                <select
                  value={productCategoryFilter}
                  onChange={(e) => setProductCategoryFilter(e.target.value)}
                  className="rounded-xl border border-white/10 bg-[#15171b] px-3 py-2 text-xs font-bold text-white [color-scheme:dark]"
                >
                  <option value="all">Toutes categories</option>
                  {productCategories.map((category) => <option key={category} value={category}>{category}</option>)}
                </select>
              </div>
            </Card>
            {filteredProducts.map((product) => (
              <Card key={product.id} className="p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="h-14 w-14 shrink-0 overflow-hidden rounded-2xl bg-white/5 text-primary">
                      {product.image_url ? (
                        <img src={product.image_url} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <span className="flex h-full w-full items-center justify-center"><Package size={22} weight="duotone" /></span>
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="truncate font-heading text-lg font-black">{product.name}</div>
                      <div className="mt-1 text-xs text-muted-foreground">{product.description || product.category || "Produit"}</div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <Badge tone="primary">{formatCDF(product.price)}</Badge>
                        <Badge>{product.category || "General"}</Badge>
                      </div>
                    </div>
                  </div>
                  <button onClick={() => deleteProduct(product.id)} className="rounded-full bg-destructive/15 p-2 text-destructive">
                    <Trash size={17} />
                  </button>
                </div>
              </Card>
            ))}
            {products.length === 0 && <Card className="p-6 text-center text-sm text-muted-foreground">Aucun produit pour ce partenaire.</Card>}
            {products.length > 0 && filteredProducts.length === 0 && <Card className="p-6 text-center text-sm text-muted-foreground">Aucun produit ne correspond aux filtres.</Card>}
          </div>
        </div>
      )}

      {tab === "orders" && (
        <div className="mt-6 grid gap-3">
          {orders.map((order) => (
            <Card key={order.id} className="p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-heading text-lg font-black">{order.merchant_name}</div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {order.customer_name} - {order.recipient_phone} - {order.items?.length || 0} article(s)
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <StatusBadge status={order.status} />
                    <StatusBadge status={order.payment_status || "PENDING"} />
                    <Badge tone="primary">{formatCDF(order.total_amount)}</Badge>
                    <Badge>{order.payment_method}</Badge>
                    {order.merchant_payment_phone && <Badge tone="success">{order.merchant_payment_phone}</Badge>}
                    {order.ride_id && <Badge tone="success">Ride #{order.ride_id.slice(0, 6).toUpperCase()}</Badge>}
                  </div>
                  {order.payment_proof_url && (
                    <a
                      href={order.payment_proof_url}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-3 inline-flex w-fit rounded-full bg-white/10 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-primary"
                    >
                      Voir capture paiement
                    </a>
                  )}
                  {order.payment_proof_reference && (
                    <div className="mt-2 text-xs text-muted-foreground">Reference: {order.payment_proof_reference}</div>
                  )}
                  <div className="mt-3 rounded-xl bg-white/5 p-3 text-xs text-muted-foreground">
                    {(order.items || []).map((item) => `${item.quantity}x ${item.name}`).join(", ")}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {order.payment_method !== "cash" && order.payment_status === "PROOF_SUBMITTED" && (
                    <>
                      <Button onClick={() => confirmPayment(order.id)} className="min-h-9 px-3 py-2 text-xs">Paiement OK</Button>
                      <Button variant="outline" onClick={() => rejectPayment(order.id)} className="min-h-9 px-3 py-2 text-xs">Refuser preuve</Button>
                    </>
                  )}
                  {["PENDING_MERCHANT", "MERCHANT_CONFIRMED"].includes(order.status) && (
                    <Button onClick={() => confirmOrder(order.id)} className="min-h-9 px-3 py-2 text-xs">Confirmer</Button>
                  )}
                  {!["DELIVERED", "CANCELLED"].includes(order.status) && (
                    <Button variant="danger" onClick={() => cancelOrder(order.id)} className="min-h-9 px-3 py-2 text-xs">Annuler</Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
          {orders.length === 0 && <Card className="p-8 text-center text-sm text-muted-foreground">Aucune commande marketplace.</Card>}
        </div>
      )}

      {tab === "agents" && (
        <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_1.2fr]">
          <Card className="p-5">
            <h2 className="font-heading text-2xl font-black">{editingAgentId ? "Modifier agent" : "Ajouter agent"}</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Choisissez les options de travail autorisees pour cet agent. Son espace affichera uniquement les modules permis.
            </p>
            <div className="mt-4 grid gap-3">
              <div>
                <Label>Nom</Label>
                <Input value={agentForm.name} onChange={(e) => setAgentForm({ ...agentForm, name: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Telephone</Label>
                  <Input value={agentForm.phone} onChange={(e) => setAgentForm({ ...agentForm, phone: e.target.value })} />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input value={agentForm.email} onChange={(e) => setAgentForm({ ...agentForm, email: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Ville</Label>
                  <select value={agentForm.city_id} onChange={(e) => setAgentForm({ ...agentForm, city_id: e.target.value })} className="w-full rounded-xl border border-white/10 bg-background px-4 py-3 text-sm">
                    {cities.map((city) => <option key={city.id} value={city.id}>{city.name}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Titre</Label>
                  <Input value={agentForm.title} onChange={(e) => setAgentForm({ ...agentForm, title: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Permissions</Label>
                <div className="grid gap-2">
                  {AGENT_PERMISSION_OPTIONS.map((permission) => {
                    const selected = agentForm.permissions?.includes(permission.key);
                    return (
                      <button
                        key={permission.key}
                        type="button"
                        onClick={() => toggleAgentPermission(permission.key)}
                        className={`rounded-2xl border p-3 text-left transition ${selected ? "border-primary/70 bg-primary/15" : "border-white/10 bg-white/5 hover:bg-white/10"}`}
                      >
                        <div className="flex items-start gap-3">
                          <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${selected ? "border-primary bg-primary text-primary-foreground" : "border-white/20"}`}>
                            {selected ? "OK" : ""}
                          </span>
                          <span>
                            <span className="block text-sm font-black">{permission.label}</span>
                            <span className="mt-0.5 block text-xs leading-5 text-muted-foreground">{permission.desc}</span>
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
              <label className="flex items-center gap-2 rounded-2xl border border-white/10 bg-white/5 p-3 text-sm font-bold">
                <input
                  type="checkbox"
                  checked={agentForm.active}
                  onChange={(e) => setAgentForm({ ...agentForm, active: e.target.checked })}
                />
                Agent actif
              </label>
              <Input value={agentForm.notes} onChange={(e) => setAgentForm({ ...agentForm, notes: e.target.value })} placeholder="Notes internes" />
              <Button onClick={submitAgent}><UserGear size={18} weight="bold" /> {editingAgentId ? "Sauvegarder agent" : "Ajouter agent"}</Button>
              {editingAgentId && (
                <Button variant="outline" onClick={() => { setEditingAgentId(""); setAgentForm(emptyAgent(cities[0]?.id || "")); }}>
                  Annuler modification
                </Button>
              )}
            </div>
          </Card>

          <div className="grid gap-3">
            {agents.map((agent) => (
              <Card key={agent.id} className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-heading text-lg font-black">{agent.name}</div>
                    <div className="mt-1 text-xs text-muted-foreground">{agent.title} - {agent.city_name} - {agent.phone}</div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <Badge tone={agent.active === false ? "danger" : "success"}>{agent.active === false ? "Inactif" : "Actif"}</Badge>
                      {(agent.permissions || []).map((permission) => <Badge key={permission}>{permissionLabel(permission)}</Badge>)}
                    </div>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <button onClick={() => editAgent(agent)} className="rounded-full bg-white/10 p-2">
                      <PencilSimple size={17} />
                    </button>
                    <button onClick={() => disableAgent(agent.id)} className="rounded-full bg-destructive/15 p-2 text-destructive">
                      <Trash size={17} />
                    </button>
                  </div>
                </div>
              </Card>
            ))}
            {agents.length === 0 && <Card className="p-8 text-center text-sm text-muted-foreground">Aucun agent configure.</Card>}
          </div>
        </div>
      )}
    </AdminShell>
  );
}
